import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import type { Locale } from 'date-fns';

export function dateLocale(lang: string): Locale {
  return lang.startsWith('en') ? enUS : tr;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}

export function formatDateTime(value: string | null | undefined, lang: string): string {
  const d = toDate(value);
  return d ? format(d, 'd MMM yyyy HH:mm', { locale: dateLocale(lang) }) : '—';
}

export function formatDate(value: string | null | undefined, lang: string): string {
  const d = toDate(value);
  return d ? format(d, 'd MMM yyyy', { locale: dateLocale(lang) }) : '—';
}

/** Hours and minutes ("14:05"), for "valid until" times. */
export function formatHourMinute(value: string | null | undefined, lang: string): string {
  const d = toDate(value);
  return d ? format(d, 'HH:mm', { locale: dateLocale(lang) }) : '—';
}

export function formatTime(value: string | null | undefined, lang: string): string {
  const d = toDate(value);
  return d ? format(d, 'HH:mm:ss', { locale: dateLocale(lang) }) : '—';
}

export function formatRelative(value: string | null | undefined, lang: string): string {
  const d = toDate(value);
  return d ? formatDistanceToNowStrict(d, { addSuffix: true, locale: dateLocale(lang) }) : '—';
}

/** Clock-style duration: 00:45, 03:07, 1:02:03. Language neutral, set in mono. */
export function formatClock(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** Offset with tenths, for replay positions: 02:13.4 */
export function formatOffset(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  const clamped = Math.max(0, ms);
  const tenths = Math.floor((clamped % 1000) / 100);
  return `${formatClock(clamped)}.${tenths}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** ISO string -> value for <input type="datetime-local"> (local time). */
export function isoToLocalInput(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d ? format(d, "yyyy-MM-dd'T'HH:mm") : '';
}

/** <input type="datetime-local"> value -> ISO UTC string, or null when empty. */
export function localInputToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? d.toISOString() : null;
}

/** Money in Turkish lira (or another ISO currency): ₺1.068,00 / TRY 1,068.00. */
export function formatMoney(amount: number | null | undefined, lang: string, currency = 'TRY'): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat(lang.startsWith('en') ? 'en-US' : 'tr-TR', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}
